<template>
  <div class="main-container">
    <div class="nav-action">
      <ul class="wordcloud-action">
        <li class="nav-item">
          <a class="nav-link" @click="openConfirmReset()">Reset</a>
        </li>
        <li class="nav-item">
          <a class="nav-link hidden xs:inline" @click="download()">Download</a>
          <a class="nav-link inline xs:hidden" @click="downloadHighRes()">Download</a>
        </li>
        <li class="nav-item">
          <a class="nav-link" @click="save()">Save</a>
        </li>
      </ul>
    </div>
    <div class="input-container">
      <textarea
        rows="1"
        ref="textarea"
        placeholder="Enter your text (separate the words using a comma, semicolon or new line)…"
      />
      <aha-button variant="base" @click="generate">Generate</aha-button>
    </div>
    <WordCloud :options="options" />
    <ConfirmReset
      v-if="showConfirmReset"
      @close="handleCloseConfirmNewDataPopup"
      @reset="handleReset"
    />
  </div>
</template>

<script>

import util from '@/utils'
import EventBus from '@/eventBus'
import { toPng } from 'html-to-image';
import autosize from 'autosize';
import Constant from '@/constants'

import WordCloud from '@/components/WordCloud'
import WordCloudContainer from '@/containers/WordCloudContainer'
import ConfirmReset from '@/components/ConfirmReset.vue'

const DOWNLOAD_FILE_NAME = 'AhaSlides_WordCloud'

export default {
  name: 'App',
  components: {
    WordCloud: WordCloudContainer(WordCloud),
    ConfirmReset,
  },
  data() {
    return {
      showConfirmReset: false,
      defaultWords: Constant.defaultWords,
      words: []
    }
  },
  computed: {
    options() {
      if (!this.words.length) return this.defaultWords.map(w => ({ title: w }))
      return this.words.map(w => ({ title: w }))
    }
  },
  created() {
    EventBus.$on('update-words', this.updateWords)
    this.$nextTick(() => {
      autosize(this.$refs.textarea)
      this.handleResizeEvent(this.$refs.textarea)

      // update iframe size initially
      // due to diferent input sizes on different screens
      window.parent.postMessage({
        'func': 'parentFunc',
        'message': 'updateInputSize',
        'inputSize': this.$refs.textarea.offsetHeight,
      }, "*");
    })
  },
  methods: {
    handleResizeEvent(el) {
      el.addEventListener('autosize:resized', (e) => {
        window.parent.postMessage({
          'func': 'parentFunc',
          'message': 'updateInputSize',
          'inputSize': e.target.offsetHeight,
        }, "*");
      })
    },
    openConfirmReset() {
      this.showConfirmReset = true
      util.gaTrackEvent(
        'Word Cloud Page',
        'Word Cloud > Open Confirm Reset',
      )
    },
    save() {
      const data = encodeURIComponent(JSON.stringify(this.options))
      window.parent.postMessage({
        'func': 'parentFunc',
        'message': 'save word cloud',
        'url': `/save-presentation/?wordcloud=true&wordcloud-data=${data}`
      }, "*");
      util.gaTrackEvent(
        'Word Cloud Page',
        'Word Cloud > Save data',
      )
    },
    download() {
      util.gaTrackEvent(
        'Word Cloud Page',
        'Word Cloud > Download image',
      )
      toPng(document.querySelector('.wordcloud-container'))
        .then(function (dataUrl) {
          var link = document.createElement('a');
          link.download = DOWNLOAD_FILE_NAME;
          link.href = dataUrl;
          link.click();
        });
    },
    downloadHighRes() {
      util.gaTrackEvent(
        'Word Cloud Page',
        'Word Cloud > Download image',
      )
      toPng(document.querySelector('.wordcloud-container'), { pixelRatio: 10 })
        .then(function (dataUrl) {
          var link = document.createElement('a');
          link.download = DOWNLOAD_FILE_NAME;
          link.href = dataUrl;
          link.click();
        });
    },
    generate() {
      const words = this.$refs.textarea.value
      if (words) {
        this.words = this.words.concat(this.parseEntries(words))
      }
      this.$refs.textarea.value = ''
      this.$nextTick(() => {
        autosize.update(this.$refs.textarea)
      })
      this.draw()
      util.gaTrackEvent(
        'Word Cloud Page',
        'Word Cloud > Generate',
      )
    },
    draw() {
      // wait a bit to get latest data first before render
      this.$nextTick(() => {
        EventBus.$emit('draw-wordcloud')
      })
    },
    handleCloseConfirmNewDataPopup() {
      this.showConfirmReset = false
    },
    handleReset() {
      this.words = []
      this.handleCloseConfirmNewDataPopup()
      this.draw()
    },
    updateWords(words) {
      this.words = words.map(w => w.title)
      this.draw()
    },
    onChangeTextArea(e) {
      this.words = this.parseEntries(e.target.value)
    },
    // input: 1@a.com,2@a.com ,3@a.com, 4@a.com; 5@a.com;6@a.com ;7@x.com 8@x.com     9@x.com ,      10@x.com ee,ae
    // output: [1@a.com, 2@a.com, 3@a.com, 4@a.com, 5@a.com, 6@a.com, 7@x.com, 8@x.com, 9@x.com, 10@x.com, ee, ae]
    parseEntries(string) {
      // look for: , and ; and newline character
      const regex = /(\s*,\s*|\s*;\s*|\s*\n\s*)/ig
      const entries = string.replace(regex, ',')
      // split by ,
      return entries.split(',').filter(e => !!e)
    },
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.input-container {
  width: 500px;
  max-width: 100%;
  display: flex;
  align-items: baseline;
  justify-content: center;
  border-radius: 8px;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    font-size: 12px;
  }

  textarea {
    flex: 1;
    padding: 8px;
    background: #ffffff;
    border: 1px solid #d3d7e1;
    border-radius: 4px;
    margin: 0px 12px;
    min-height: 58px; // fix firefox initial placeholder got truncated
    @media (max-width: 370px) {
      min-height: 78px;
    }
    font-size: 12px;
    font-weight: inherit;
    line-height: 1.2;
    @apply appearance-none;
    @apply px-2 h-10 text-sm w-full border border-secondary-20;
    @apply focus:bg-primary-20 focus:outline-none;
    @apply hover:bg-primary-20;
    @apply placeholder-base-70;
  }

  .aha-button {
    font-size: 16px;
    margin-top: -5px;
    @media (max-width: 480px) {
      font-size: 12px;
    }
  }
}

.main-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.wordcloud-action {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px 0;
  width: 280px;
  background: #348ef6;
  border-radius: 22px;
  position: relative;
  margin-bottom: 32px;

  @media (max-width: 480px) {
    width: 250px;
    font-size: 12px;
    margin-bottom: 12px;
  }

  .nav-item {
    cursor: pointer;
    position: relative;
    flex: 1;
    color: white;
    font-weight: bold;
    text-align: center;

    &:hover {
      text-decoration: underline;
    }
  }

  .nav-item:not(:last-child) .nav-link {
    &:after {
      content: "";
      opacity: 0.3;
      top: 0;
      right: 0;
      width: 1px;
      height: 100%;
      position: absolute;
      display: inline-block;
      background: #d3d7e1;
    }
  }

  .nav-item:first-child .nav-link {
    &:after {
      right: 7px;
      @media (max-width: 480px) {
        right: 5px;
      }
    }
  }
  .nav-item:nth-child(2) .nav-link {
    &:after {
      right: -12px;
      @media (max-width: 480px) {
        right: -10px;
      }
    }
  }
}
</style>
